import type { SessionService, SessionStatus } from './session-service';

const DEFAULT_ATTEMPTS = 2;
const DEFAULT_RETRY_DELAY_MS = 750;

export interface ResolveSessionAtBootOptions {
  session: SessionService;
  attempts?: number;
  retryDelayMs?: number;
  sleep?: (ms: number) => Promise<unknown>;
}

/**
 * The session read the app boots on.
 *
 * `unknown` (offline, Kratos hiccup) is retried rather than treated as a logout:
 * the pre-PSTUDIO-4022 boot code signed the user out on any failed `toSession()`,
 * so a flaky moment at load time cost them their session. An `expired` answer is
 * final — Kratos has spoken, retrying it only delays the login redirect.
 */
export async function resolveSessionAtBoot({
  session,
  attempts = DEFAULT_ATTEMPTS,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  sleep = (ms: number) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    }),
}: ResolveSessionAtBootOptions): Promise<SessionStatus> {
  let status: SessionStatus = { state: 'unknown' };

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    status = await session.revalidate();

    if (status.state !== 'unknown') return status;

    if (attempt < attempts) await sleep(retryDelayMs);
  }

  return status;
}
